import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Clock, Printer, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { eur } from "../lib/format";
import { useAuth } from "../auth";
import * as apiClient from "../api";

export default function Store() {
  const navigate = useNavigate();
  const { doLogout } = useAuth();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [participants, setParticipants] = useState(2);
  const [customer, setCustomer] = useState({ name: "", surname: "", email: "", phone: "" });
  const [booking, setBooking] = useState(null);

  const { data: experiences = [] } = useQuery({ queryKey: ["storeExp", q], queryFn: () => apiClient.getExperiences({ q: q || undefined, limit: 30 }) });
  const { data: availability = [] } = useQuery({ queryKey: ["storeAvail", selected?.slug], queryFn: () => apiClient.getAvailability(selected.slug), enabled: !!selected });

  const daySlots = availability.find((a) => a.date === date)?.slots || [];

  const submit = async () => {
    if (!selected || !date || !time || !customer.name) { toast.error("Compila tutti i campi"); return; }
    try {
      const b = await apiClient.createBooking({
        items: [{ experience_id: selected.id, date, time, participants }],
        customer: { ...customer, language: "it", country: "IT" }, payment_method: "store",
      });
      setBooking(b);
      toast.success("Prenotazione creata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const reset = () => { setBooking(null); setSelected(null); setDate(""); setTime(""); setParticipants(2); setCustomer({ name: "", surname: "", email: "", phone: "" }); };

  if (booking) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl max-w-lg w-full overflow-hidden" data-testid="store-voucher">
          <div className="bg-primary text-white p-5 flex items-center justify-between"><Logo light /><span className="font-heading font-bold">{booking.booking_number}</span></div>
          <div className="p-6 text-center">
            <img src={booking.qr} alt="QR" className="h-44 w-44 mx-auto" />
            <h2 className="mt-4 font-heading font-semibold text-xl text-slate-900">{booking.items[0].title}</h2>
            <p className="text-slate-500">{booking.items[0].date} · {booking.items[0].time} · {booking.items[0].participants}p</p>
            <p className="text-2xl font-heading font-bold text-primary mt-2">{eur(booking.gmv)}</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => window.print()} className="flex-1 rounded-full bg-primary text-white font-semibold py-3 flex items-center justify-center gap-2"><Printer className="h-4 w-4" /> Stampa voucher</button>
              <button onClick={reset} className="flex-1 rounded-full border border-slate-200 font-semibold py-3">Nuova prenotazione</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-[#0A1128] text-white px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4"><Logo light /><span className="text-xs uppercase tracking-widest text-white/50">Store Mode · Vieste</span></div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/gestione")} className="text-sm text-white/60 hover:text-white">Gestione</button>
          <button onClick={async () => { await doLogout(); navigate("/"); }} className="text-sm text-white/60 hover:text-white">Esci</button>
        </div>
      </header>

      <div className="grid lg:grid-cols-2 gap-6 p-6 max-w-6xl mx-auto">
        {/* Left: search + select */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 mb-4">
            <Search className="h-5 w-5 text-slate-400" />
            <input autoFocus data-testid="store-search" placeholder="Cerca esperienza..." value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 bg-transparent outline-none text-lg" />
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto no-scrollbar">
            {experiences.map((e) => (
              <button key={e.id} data-testid={`store-exp-${e.slug}`} onClick={() => { setSelected(e); setDate(""); setTime(""); }} className={`w-full flex items-center gap-3 rounded-xl p-2.5 text-left transition-colors ${selected?.id === e.id ? "bg-primary/10 ring-2 ring-primary" : "hover:bg-slate-50"}`}>
                <img src={e.images[0]} alt="" className="h-12 w-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><div className="font-semibold text-slate-800 line-clamp-1">{e.title}</div><div className="text-xs text-slate-400">{e.operator_name} · {eur(e.price)}</div></div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: booking form */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-slate-400 min-h-[300px]">Seleziona un'esperienza per iniziare</div>
          ) : (
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-xl text-slate-900">{selected.title}</h2>
              <div>
                <label className="text-xs font-bold uppercase text-slate-400">Data (prossimi giorni)</label>
                <div className="grid grid-cols-6 gap-1.5 mt-1">
                  {availability.slice(0, 12).map((a) => (
                    <button key={a.date} data-testid={`store-date-${a.date}`} onClick={() => { setDate(a.date); setTime(""); }} className={`rounded-lg py-2 text-center text-xs ${date === a.date ? "bg-primary text-white" : "bg-slate-50"}`}>{a.date.slice(5)}</button>
                  ))}
                </div>
              </div>
              {date && (
                <div>
                  <label className="text-xs font-bold uppercase text-slate-400">Orario</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {daySlots.map((s) => <button key={s.time} disabled={s.available <= 0} onClick={() => setTime(s.time)} className={`rounded-lg px-3 py-2 text-sm font-semibold ${s.available <= 0 ? "bg-slate-50 text-slate-300" : time === s.time ? "bg-primary text-white" : "bg-slate-50"}`}>{s.time}</button>)}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="text-xs font-bold uppercase text-slate-400">Partecipanti</label>
                <button onClick={() => setParticipants((p) => Math.max(1, p - 1))} className="h-9 w-9 rounded-full border font-bold">−</button>
                <span className="font-bold text-lg w-8 text-center">{participants}</span>
                <button onClick={() => setParticipants((p) => p + 1)} className="h-9 w-9 rounded-full border font-bold">+</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Nome" data-testid="store-cust-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Cognome" value={customer.surname} onChange={(e) => setCustomer({ ...customer, surname: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <input placeholder="Telefono" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-slate-500">Totale</span>
                <span className="text-2xl font-heading font-bold text-primary">{eur(selected.pricing_type === "per_person" ? selected.price * participants : selected.price)}</span>
              </div>
              <button onClick={submit} data-testid="store-book-btn" className="w-full rounded-full bg-primary text-white font-semibold py-3.5 flex items-center justify-center gap-2"><Check className="h-5 w-5" /> Prenota & genera voucher</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
